// Impresión de tickets TPV: Bluetooth clásico nativo (app Capacitor),
// Web Bluetooth BLE (navegador) o diálogo de impresión del sistema (fallback).
import { money } from '../events/util';
import QRCode from 'qrcode';
import { Capacitor, registerPlugin } from '@capacitor/core';

// Plugin nativo (ver android/.../ThermalPrinterPlugin.java). En web es un stub inerte.
const ThermalPrinter = registerPlugin('ThermalPrinter');

// UUIDs comunes de impresoras térmicas Bluetooth (BLE)
const COMMON_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', // genérico térmico
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 / serial BLE
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // ISSC
];

export function bluetoothAvailable() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

// Empareja una impresora y devuelve la characteristic escribible
export async function connectBluetoothPrinter() {
  if (!bluetoothAvailable()) throw new Error('NO_BT');
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: COMMON_SERVICES,
  });
  const server = await device.gatt.connect();
  const services = await server.getPrimaryServices();
  for (const svc of services) {
    const chars = await svc.getCharacteristics();
    for (const c of chars) {
      if (c.properties.write || c.properties.writeWithoutResponse) {
        return { device, characteristic: c };
      }
    }
  }
  throw new Error('NO_WRITABLE');
}

// ESC/POS mínimo
const ESC = 0x1b, GS = 0x1d;

// Carga una imagen del mismo origen
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Rasteriza el logo a bitmap 1-bit (negro sólido) para impresora térmica.
// Devuelve bytes ESC/POS (GS v 0) centrados, o null si falla.
async function rasterizeLogo(url, maxWidth = 360) {
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, maxWidth / img.width);
    let w = Math.max(8, Math.round(img.width * scale));
    w = w - (w % 8); // múltiplo de 8
    const h = Math.round(img.height * (w / img.width));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h); // sin fondo: trabajamos sobre el canal alfa
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const bytesPerRow = w / 8;
    const raster = new Uint8Array(bytesPerRow * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const a = data[idx + 3];
        // Logo PNG transparente: imprimimos la silueta (cualquier pixel opaco).
        if (a > 128) raster[y * bytesPerRow + (x >> 3)] |= (0x80 >> (x & 7));
      }
    }
    const header = [ESC, 0x61, 0x01, GS, 0x76, 0x30, 0x00,
      bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff, h & 0xff, (h >> 8) & 0xff];
    const out = new Uint8Array(header.length + raster.length + 1);
    out.set(header, 0); out.set(raster, header.length); out[out.length - 1] = 0x0a;
    return out;
  } catch { return null; }
}

// Logo de marca al pie de cada recibo ("powered by"). Versión optimizada para
// impresión térmica (silueta limpia sobre transparente). Bundleado en la app.
const BRAND_FOOTER_LOGO = '/assets/tukipass-ticket.png';

// Codifica solo las líneas de texto (sin avance ni corte).
function encodeLines(lines) {
  const out = [];
  for (const ln of lines) {
    if (ln.center) out.push(ESC, 0x61, 0x01); else out.push(ESC, 0x61, 0x00);
    if (ln.big) out.push(GS, 0x21, 0x11); else out.push(GS, 0x21, 0x00);
    if (ln.bold) out.push(ESC, 0x45, 0x01); else out.push(ESC, 0x45, 0x00);
    const text = (ln.text || '') + '\n';
    for (const ch of new TextEncoder().encode(text)) out.push(ch);
  }
  return new Uint8Array(out);
}

// Avance final + corte parcial de papel.
const FEED_AND_CUT = new Uint8Array([0x0a, 0x0a, 0x0a, GS, 0x56, 0x42, 0x00]);

// Construye el flujo ESC/POS completo: init + logo arriba + contenido +
// logo de marca al pie + corte.
// `footerQrUrl` es el QR de ARCA del comprobante fiscal: va DESPUÉS del texto y antes
// de la firma de marca, que es donde se lo espera en un ticket.
export async function buildTicketBytes(lines, logoUrl, brandLogoUrl = BRAND_FOOTER_LOGO, topWidth = 180, headerLogoUrl = null, headerWidth = 240, footerQrUrl = null) {
  const parts = [new Uint8Array([ESC, 0x40])]; // init
  if (headerLogoUrl) {
    const hdr = await rasterizeLogo(headerLogoUrl, headerWidth); // logo del venue, por encima del QR
    if (hdr) parts.push(hdr);
  }
  if (logoUrl) {
    const logo = await rasterizeLogo(logoUrl, topWidth); // imagen de arriba (logo o QR)
    if (logo) parts.push(logo);
  }
  parts.push(encodeLines(lines));
  if (footerQrUrl) {
    const qr = await rasterizeLogo(footerQrUrl, 260);
    if (qr) parts.push(qr);
  }
  if (brandLogoUrl) {
    const brand = await rasterizeLogo(brandLogoUrl, 240); // más chico: firma al pie
    if (brand) parts.push(brand);
  }
  parts.push(FEED_AND_CUT);
  const len = parts.reduce((a, p) => a + p.length, 0);
  const data = new Uint8Array(len);
  let off = 0; for (const p of parts) { data.set(p, off); off += p.length; }
  return data;
}

async function sendBle(characteristic, data) {
  const chunk = 180;
  for (let i = 0; i < data.length; i += chunk) {
    const slice = data.slice(i, i + chunk);
    if (characteristic.properties.writeWithoutResponse) await characteristic.writeValueWithoutResponse(slice);
    else await characteristic.writeValue(slice);
    await new Promise(r => setTimeout(r, 20));
  }
}

export async function printBluetooth(characteristic, lines, logoUrl, footerQrUrl = null) {
  await sendBle(characteristic, await buildTicketBytes(lines, logoUrl, BRAND_FOOTER_LOGO, 180, null, 240, footerQrUrl));
}

// ── Impresión nativa por Bluetooth clásico (SPP), solo en la app Capacitor ──

export function nativePrinterAvailable() {
  return Capacitor.isNativePlatform();
}

function bytesToBase64(bytes) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

// Lista las impresoras emparejadas en Android → [{ name, address }]
export async function listNativePrinters() {
  const { devices } = await ThermalPrinter.listPaired();
  return Array.isArray(devices) ? devices : [];
}

export async function connectNativePrinter(address) {
  return ThermalPrinter.connect({ address });
}

export async function nativeIsConnected() {
  try { const r = await ThermalPrinter.isConnected(); return !!r.connected; }
  catch { return false; }
}

// ¿Este fallo es "el vínculo Bluetooth se cayó"? Un socket muerto da "Broken pipe";
// también cuentan los avisos de no-conectado del plugin. NO incluye TIMEOUT: un
// timeout puede ser una impresora lenta que SÍ está imprimiendo, y reintentar ahí
// saca un segundo ticket con el mismo número de percha, que es peor que no sacar
// ninguno.
function esVinculoCaido(e) {
  const m = String((e && (e.message || e.errorMessage)) || e || '').toLowerCase();
  const code = String((e && (e.code || e.errorCode)) || '').toUpperCase();
  if (code === 'NOT_CONNECTED' || code === 'WRITE_FAILED') return true;
  return m.includes('broken pipe') || m.includes('socket')
    || m.includes('no conectada')          // el plugin responde en castellano
    || m.includes('not connected') || m.includes('no_printer')
    || m.includes('closed') || m.includes('econnreset');
}

// Imprime un ticket por SPP, reconectando a la dirección guardada si hace falta.
//
// La noche del 8/8 hubo 2 fallos "Broken pipe": el socket muere DURANTE la escritura y
// en ese momento isConnected() suele seguir diciendo que está conectada (socket
// zombi), así que la reconexión de arriba no se dispara y el ticket no sale. Ahora, si
// la escritura falla por vínculo caído, se fuerza la reconexión y se reintenta UNA vez.
// onReintento: se llama si la primera escritura falló por vínculo caído y hubo que
// reconectar. Sirve para que quede en la caja negra que la impresora se cayó, aunque
// el ticket haya salido — si no, un problema real de Bluetooth se vuelve invisible.
export async function printNativeSPP(lines, logoUrl, address, onReintento, footerQrUrl = null) {
  if (!(await nativeIsConnected())) {
    if (!address) throw new Error('NO_PRINTER');
    await connectNativePrinter(address);
  }
  const data = await buildTicketBytes(lines, logoUrl, BRAND_FOOTER_LOGO, 180, null, 240, footerQrUrl);
  const payload = bytesToBase64(data);
  try {
    await ThermalPrinter.write({ data: payload });
  } catch (e) {
    if (!esVinculoCaido(e) || !address) throw e;
    // Reconexión forzada: no se le pregunta a isConnected() porque es justo lo que
    // mintió. Si tampoco entra, se propaga el error y el cajero se entera.
    try { await ThermalPrinter.disconnect(); } catch { /* puede ya estar cerrado */ }
    await connectNativePrinter(address);
    await ThermalPrinter.write({ data: payload });
    try { if (onReintento) onReintento(e); } catch { /* nunca romper por telemetría */ }
  }
}

// Encabezado/pie resueltos desde la config del ticket del menú
function ticketHeader(session) {
  const t = session.ticket || {};
  if (t.headerType === 'custom' && t.headerText) return t.headerText;
  return session.eventName || session.orgName || '';
}

// Líneas del ticket según tipo de caja
// `variant` solo aplica al guardarropa, donde se imprimen DOS tickets distintos:
//  · 'cliente' → número grande + "presentar para retirar" (es su comprobante)
//  · 'percha'  → número grande + la descripción de la prenda (es lo que ayuda a
//     encontrarla en el perchero); NO lleva el precio, va colgado a la vista.
// El número es el de PERCHA elegido en la grilla, no un correlativo de impresión:
// por eso reimprimir no cambia nada y quedarse sin papel no genera números fantasma.
export function ticketLines(sale, session, variant = 'cliente') {
  const isGuarda = session.menuType === 'guardarropa';
  const t = session.ticket || {};
  const lines = [];
  const header = ticketHeader(session);
  if (header) lines.push({ text: header, center: true, bold: true, big: true });
  lines.push({ text: '--------------------------------', center: true });
  if (isGuarda) {
    const num = sale.hanger_number != null ? String(sale.hanger_number) : String(sale.ticket_number || 0).padStart(3, '0');
    const esPercha = variant === 'percha';
    // EL PUESTO VA SIEMPRE, en los dos tickets. La numeración es por puesto (cada uno
    // tiene su 1..N), así que el número solo no alcanza: el cliente tiene que saber a
    // qué puesto volver, y si un abrigo aparece en el puesto equivocado hay que poder
    // devolverlo al suyo.
    const puesto = session.boxName || '';
    lines.push({ text: esPercha ? 'PERCHA' : 'GUARDARROPA', center: true, bold: true });
    if (puesto) lines.push({ text: puesto.toUpperCase(), center: true, bold: true, big: true });
    lines.push({ text: num, center: true, big: true, bold: true });
    if (esPercha) {
      // Lo que importa en el perchero: qué prenda es.
      if (sale.note) { lines.push({ text: '' }); lines.push({ text: sale.note, center: true, bold: true }); }
      lines.push({ text: '' });
      lines.push({ text: 'No entregar sin ticket', center: true });
    } else {
      if (sale.note) lines.push({ text: sale.note, center: true });
      lines.push({ text: '' });
      lines.push({ text: 'PRESENTAR PARA RETIRAR', center: true, bold: true });
      lines.push({ text: '' });
      lines.push({ text: 'Total: ' + money(sale.total), center: true, bold: true });
    }
  } else {
    for (const it of (sale.items || [])) {
      lines.push({ text: `${it.qty} x ${it.name}` });
      lines.push({ text: '   ' + money(it.price * it.qty) });
    }
    lines.push({ text: '--------------------------------' });
    if (Number(sale.surcharge) > 0) {
      lines.push({ text: 'Subtotal: ' + money(Number(sale.total) - Number(sale.surcharge)) });
      lines.push({ text: `Recargo QR: ${money(Number(sale.surcharge))}` });
    }
    lines.push({ text: 'TOTAL: ' + money(sale.total), bold: true, big: true });
    if (sale.payment_method) lines.push({ text: 'Pago: ' + (sale.payment_method === 'qr' ? 'QR' : 'EFECTIVO'), bold: true });
    // COMPROBANTE FISCAL. Los datos van impresos aunque esté el QR: el QR no los
    // reemplaza, lo dice la resolución que lo hizo obligatorio.
    const c = sale.comprobante;
    if (c) {
      lines.push({ text: '--------------------------------' });
      lines.push({ text: `${c.nombre} ${c.numero}`, bold: true });
      if (c.neto != null) lines.push({ text: `Neto ${money(c.neto)} + IVA ${money(c.iva)}` });
      lines.push({ text: 'CAE ' + c.cae });
      if (c.cae_vto) lines.push({ text: 'Vto. CAE ' + c.cae_vto });
    }
  }
  if (t.footer) { lines.push({ text: '' }); lines.push({ text: t.footer, center: true }); }
  lines.push({ text: new Date(sale.created_at || Date.now()).toLocaleString('es-AR'), center: true });
  // Atribución "powered by": nombre del venue (identidad del home) + logo tukipass al pie.
  const venue = session.heroName || session.orgName || '';
  lines.push({ text: '' });
  if (venue) lines.push({ text: `${venue} gestiona su ticketing, barra y administracion con`, center: true });
  return lines;
}

// Fallback: diálogo de impresión del navegador
export function printBrowser(sale, session, variant = 'cliente') {
  const isGuarda = session.menuType === 'guardarropa';
  // Guardarropa: el número es el de PERCHA (elegido en la grilla). El ticket de
  // percha lleva la prenda y NO el precio; el del cliente lleva el precio y el aviso
  // de presentarlo para retirar.
  const num = sale.hanger_number != null ? String(sale.hanger_number) : '#' + String(sale.ticket_number || 0).padStart(3, '0');
  // El puesto va en los dos tickets: la numeración es por puesto, así que el número
  // solo no le dice al cliente adónde volver.
  const puesto = session.boxName ? `<div class="puesto">${escapeHtml(String(session.boxName).toUpperCase())}</div>` : '';
  const body = isGuarda
    ? (variant === 'percha'
      ? `<div class="big">PERCHA</div>
         ${puesto}
         <div class="num">${num}</div>
         ${sale.note ? `<div class="desc" style="font-weight:800;font-size:15px">${escapeHtml(sale.note)}</div>` : ''}
         <div class="foot">No entregar sin ticket</div>`
      : `<div class="big">GUARDARROPA</div>
         ${puesto}
         <div class="num">${num}</div>
         ${sale.note ? `<div class="desc">${escapeHtml(sale.note)}</div>` : ''}
         <div class="big">PRESENTAR PARA RETIRAR</div>
         <div class="tot">${money(sale.total)}</div>`)
    : `${(sale.items || []).map(it => `<div class="row"><span>${it.qty} × ${escapeHtml(it.name)}</span><span>${money(it.price * it.qty)}</span></div>`).join('')}
       <hr/>${Number(sale.surcharge) > 0
          ? `<div class="row"><span>Subtotal</span><span>${money(Number(sale.total) - Number(sale.surcharge))}</span></div>
             <div class="row"><span>Recargo QR</span><span>${money(Number(sale.surcharge))}</span></div>`
          : ''}
       <div class="row tot"><span>TOTAL</span><span>${money(sale.total)}</span></div>
       ${sale.payment_method ? `<div class="row"><span>Pago</span><span>${sale.payment_method === 'qr' ? 'QR' : 'EFECTIVO'}</span></div>` : ''}
       ${sale.comprobante ? `<hr/>
          <div class="row"><span>${escapeHtml(sale.comprobante.nombre)}</span><span>${escapeHtml(sale.comprobante.numero)}</span></div>
          ${sale.comprobante.neto != null ? `<div class="row"><span>Neto + IVA</span><span>${money(sale.comprobante.neto)} + ${money(sale.comprobante.iva)}</span></div>` : ''}
          <div class="row"><span>CAE</span><span>${escapeHtml(sale.comprobante.cae)}</span></div>
          ${sale.comprobante.cae_vto ? `<div class="row"><span>Vto. CAE</span><span>${escapeHtml(sale.comprobante.cae_vto)}</span></div>` : ''}` : ''}`;

  const t = session.ticket || {};
  const logoUrl = t.logo || null; // PNG transparente con el logo en negro: se imprime tal cual
  const header = (t.headerType === 'custom' && t.headerText) ? t.headerText : (session.eventName || session.orgName || '');
  const footer = t.footer || '';
  const venue = session.heroName || session.orgName || ''; // atribución "powered by"
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Ticket</title>
    <style>
      @page { size: 58mm auto; margin: 4mm; }
      body { font-family: 'Courier New', monospace; color:#000; text-align:center; width: 58mm; }
      .logo { width: 30mm; height:auto; max-height:30mm; object-fit:contain; margin:0 auto 8px; display:block; }
      .brand { font-weight:800; font-size:16px; text-transform:uppercase; margin-bottom:8px; }
      .big { font-weight:800; font-size:14px; margin-top:8px; }
      .num { font-weight:800; font-size:40px; margin:6px 0; }
      .puesto { font-weight:800; font-size:22px; margin:4px 0 0; letter-spacing:0.02em; }
      .desc { font-size:13px; margin-bottom:6px; }
      .tot { font-weight:800; font-size:18px; margin-top:8px; }
      .row { display:flex; justify-content:space-between; font-size:12px; text-align:left; }
      hr { border:0; border-top:1px dashed #000; margin:6px 0; }
      .foot { font-size:11px; margin-top:10px; }
      .ts { font-size:10px; margin-top:6px; }
      .powered { font-size:10px; margin-top:14px; line-height:1.3; }
      .powered img { width:28mm; height:auto; object-fit:contain; display:block; margin:4px auto 0; }
    </style></head><body>
      ${logoUrl ? `<img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'"/>` : ''}
      ${header ? `<div class="brand">${escapeHtml(header)}</div>` : ''}
      ${body}
      ${footer ? `<div class="foot">${escapeHtml(footer)}</div>` : ''}
      <div class="ts">${new Date(sale.created_at || Date.now()).toLocaleString('es-AR')}</div>
      <div class="powered">${venue ? `${escapeHtml(venue)} gestiona su ticketing, barra y administración con` : ''}
        <img src="/assets/tukipass-ticket.png" alt="tukipass" onerror="this.style.display='none'"/></div>
    </body></html>`;

  // Imprimir vía iframe oculto (no usa popups → no lo bloquea la tablet).
  // Abre el diálogo de impresión del sistema, donde se elige la impresora
  // configurada (USB, WiFi/Bluetooth vía servicio de impresión de Android) o PDF.
  const old = document.getElementById('tk-print-frame');
  if (old) old.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'tk-print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      const win = iframe.contentWindow;
      win.focus();
      win.print();
    } catch (e) { /* noop */ }
    setTimeout(() => { try { iframe.remove(); } catch {} }, 1500);
  };
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

// ── BOLETERÍA: un ticket con QR escaneable por entrada emitida ──

function boleteriaLines(qr, session, kind) {
  const t = session.ticket || {};
  const header = (t.headerType === 'custom' && t.headerText) ? t.headerText : (session.eventName || session.orgName || '');
  const lines = [];
  if (header) lines.push({ text: header, center: true, bold: true, big: true });
  lines.push({ text: '--------------------------------', center: true });
  if (kind === 'traslado') lines.push({ text: 'TRASLADO', center: true, bold: true });
  else if (qr.lot_name) lines.push({ text: qr.lot_name, center: true, bold: true });
  // Cumpleañero: el nombre grande debajo del lote → entrada personalizada.
  if (kind !== 'traslado' && qr.guest_name) lines.push({ text: qr.guest_name, center: true, bold: true, big: true });
  lines.push({ text: kind === 'traslado' ? 'TRASLADO · 1 USO' : 'ENTRADA · 1 USO', center: true });
  lines.push({ text: kind === 'traslado' ? qr.transport_qr : qr.qr_code, center: true, bold: true });
  if (t.footer) { lines.push({ text: '' }); lines.push({ text: t.footer, center: true }); }
  const venue = session.heroName || session.orgName || '';
  lines.push({ text: '' });
  if (venue) lines.push({ text: `${venue} gestiona su ticketing con`, center: true });
  return lines;
}

async function printOneBoleteria(code, qr, session, target, kind) {
  // QR con fondo transparente: la rasterización térmica se guía por la transparencia,
  // así solo se imprimen los módulos negros (con fondo blanco saldría un cuadro negro).
  const url = await QRCode.toDataURL(String(code), { margin: 1, width: 420, errorCorrectionLevel: 'M', color: { dark: '#000000ff', light: '#00000000' } });
  const venueLogo = session.ticket?.logo || null; // logo del boliche, arriba del QR (como barra/guardarropa)
  const bytes = await buildTicketBytes(boleteriaLines(qr, session, kind), url, BRAND_FOOTER_LOGO, 384, venueLogo); // logo + QR grande arriba
  if (target?.nativeAddr) {
    if (!(await nativeIsConnected())) await connectNativePrinter(target.nativeAddr);
    await ThermalPrinter.write({ data: bytesToBase64(bytes) });
  } else if (target?.characteristic) {
    await sendBle(target.characteristic, bytes);
  }
}

// Imprime un ticket (QR) por cada entrada emitida (+ traslado si el lote lo incluye).
// target: { nativeAddr } (app) | { characteristic } (BLE) | nada → diálogo del navegador.
export async function printBoleteriaTickets(qrCodes, session, target) {
  const list = Array.isArray(qrCodes) ? qrCodes : [];
  if (!target?.nativeAddr && !target?.characteristic) { await printBrowserBoleteria(list, session); return; }
  for (const qr of list) {
    await printOneBoleteria(qr.qr_code, qr, session, target, 'ingreso');
    if (qr.transport_qr) await printOneBoleteria(qr.transport_qr, qr, session, target, 'traslado');
  }
}

// Fallback navegador: una hoja por entrada con el QR como imagen.
export async function printBrowserBoleteria(qrCodes, session) {
  const t = session.ticket || {};
  const header = (t.headerType === 'custom' && t.headerText) ? t.headerText : (session.eventName || session.orgName || '');
  const venue = session.heroName || session.orgName || '';
  const logoUrl = t.logo || null; // logo del boliche, arriba del QR (como barra/guardarropa)
  const blocks = [];
  for (const qr of (qrCodes || [])) {
    const codes = [{ code: qr.qr_code, label: qr.lot_name || 'ENTRADA', name: qr.guest_name }];
    if (qr.transport_qr) codes.push({ code: qr.transport_qr, label: 'TRASLADO' });
    for (const c of codes) {
      const url = await QRCode.toDataURL(String(c.code), { margin: 1, width: 420 });
      blocks.push(`<div class="tk">${logoUrl ? `<img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'"/>` : ''}${header ? `<div class="brand">${escapeHtml(header)}</div>` : ''}
        <div class="lot">${escapeHtml(c.label)}</div>
        ${c.name ? `<div class="name">${escapeHtml(c.name)}</div>` : ''}
        <img class="qr" src="${url}"/>
        <div class="code">${escapeHtml(c.code)}</div>
        <div class="powered">${venue ? `${escapeHtml(venue)} gestiona su ticketing con` : ''}<img src="/assets/tukipass-ticket.png" onerror="this.style.display='none'"/></div>
      </div>`);
    }
  }
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Entradas</title><style>
    @page { size: 58mm auto; margin: 4mm; }
    body { font-family:'Courier New',monospace; color:#000; text-align:center; width:58mm; }
    .tk { page-break-after: always; padding-bottom: 6mm; }
    .logo { width:26mm; height:auto; max-height:26mm; object-fit:contain; margin:0 auto 6px; display:block; }
    .brand { font-weight:800; font-size:15px; text-transform:uppercase; margin-bottom:6px; }
    .lot { font-weight:800; font-size:14px; margin:4px 0; }
    .name { font-weight:800; font-size:17px; margin:2px 0 4px; }
    .qr { width:44mm; height:44mm; object-fit:contain; display:block; margin:6px auto; }
    .code { font-size:12px; letter-spacing:.1em; }
    .powered { font-size:10px; margin-top:10px; line-height:1.3; }
    .powered img { width:26mm; height:auto; display:block; margin:4px auto 0; }
  </style></head><body>${blocks.join('')}</body></html>`;
  const old = document.getElementById('tk-print-frame'); if (old) old.remove();
  const iframe = document.createElement('iframe'); iframe.id = 'tk-print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  iframe.onload = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ } setTimeout(() => { try { iframe.remove(); } catch {} }, 1500); };
  const doc = iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
}

// ── CONSUMICIÓN: un vale, no una entrada ──
//
// Sale del mismo TPV de boletería, pero sin QR: no hay nada que escanear porque no da
// acceso, se canjea en la barra y se retiene. Por eso tampoco lleva código: lo que vale
// es el papel. Se imprime con el logo del boliche arriba para que no sea falsificable
// con cualquier impresora, igual que el resto de los tickets.

function valeLines(vale, session) {
  const t = session.ticket || {};
  const header = (t.headerType === 'custom' && t.headerText) ? t.headerText : (session.eventName || session.orgName || '');
  const lines = [];
  if (header) lines.push({ text: header, center: true, bold: true, big: true });
  lines.push({ text: '--------------------------------', center: true });
  lines.push({ text: vale.lot_name || 'CONSUMICION', center: true, bold: true, big: true });
  lines.push({ text: 'VALE · 1 CONSUMICION', center: true });
  lines.push({ text: 'CANJEAR EN LA BARRA', center: true, bold: true });
  lines.push({ text: '--------------------------------', center: true });
  lines.push({ text: 'No es una entrada. No da acceso.', center: true });
  if (t.footer) { lines.push({ text: '' }); lines.push({ text: t.footer, center: true }); }
  const venue = session.heroName || session.orgName || '';
  lines.push({ text: '' });
  if (venue) lines.push({ text: `${venue} gestiona su ticketing con`, center: true });
  return lines;
}

// Imprime un vale por cada consumición vendida.
// target: { nativeAddr } (app) | { characteristic } (BLE) | nada → diálogo del navegador.
export async function printVales(vales, session, target) {
  const list = Array.isArray(vales) ? vales : [];
  if (!list.length) return;
  if (!target?.nativeAddr && !target?.characteristic) { await printBrowserVales(list, session); return; }
  const venueLogo = session.ticket?.logo || null;
  for (const vale of list) {
    const bytes = await buildTicketBytes(valeLines(vale, session), null, BRAND_FOOTER_LOGO, 384, venueLogo);
    if (target?.nativeAddr) {
      if (!(await nativeIsConnected())) await connectNativePrinter(target.nativeAddr);
      await ThermalPrinter.write({ data: bytesToBase64(bytes) });
    } else if (target?.characteristic) {
      await sendBle(target.characteristic, bytes);
    }
  }
}

// Fallback navegador: una hoja por vale.
export async function printBrowserVales(vales, session) {
  const t = session.ticket || {};
  const header = (t.headerType === 'custom' && t.headerText) ? t.headerText : (session.eventName || session.orgName || '');
  const venue = session.heroName || session.orgName || '';
  const logoUrl = t.logo || null;
  const blocks = (vales || []).map(v => `<div class="tk">${logoUrl ? `<img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'"/>` : ''}${header ? `<div class="brand">${escapeHtml(header)}</div>` : ''}
      <div class="lot">${escapeHtml(v.lot_name || 'CONSUMICION')}</div>
      <div class="kind">VALE · 1 CONSUMICIÓN</div>
      <div class="bar">CANJEAR EN LA BARRA</div>
      <div class="nota">No es una entrada. No da acceso.</div>
      <div class="powered">${venue ? `${escapeHtml(venue)} gestiona su ticketing con` : ''}<img src="/assets/tukipass-ticket.png" onerror="this.style.display='none'"/></div>
    </div>`);
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Consumiciones</title><style>
    @page { size: 58mm auto; margin: 4mm; }
    body { font-family:'Courier New',monospace; color:#000; text-align:center; width:58mm; }
    .tk { page-break-after: always; padding-bottom: 6mm; }
    .logo { width:26mm; height:auto; max-height:26mm; object-fit:contain; margin:0 auto 6px; display:block; }
    .brand { font-weight:800; font-size:15px; text-transform:uppercase; margin-bottom:6px; }
    .lot { font-weight:800; font-size:18px; margin:6px 0 2px; }
    .kind { font-size:12px; margin-bottom:6px; }
    .bar { font-weight:800; font-size:14px; border-top:1px dashed #000; border-bottom:1px dashed #000; padding:6px 0; margin:6px 0; }
    .nota { font-size:11px; }
    .powered { font-size:10px; margin-top:10px; line-height:1.3; }
    .powered img { width:26mm; height:auto; display:block; margin:4px auto 0; }
  </style></head><body>${blocks.join('')}</body></html>`;
  const old = document.getElementById('tk-print-frame'); if (old) old.remove();
  const iframe = document.createElement('iframe'); iframe.id = 'tk-print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  iframe.onload = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ } setTimeout(() => { try { iframe.remove(); } catch {} }, 1500); };
  const doc = iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
}
